import { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Drawer,
  Timeline,
  Descriptions,
  Switch,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { formatDateTime } from '../../utils/index';
import api from '../../utils/api';

const { TextArea } = Input;

const CommissionLevelConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [form] = Form.useForm();

  // 加载配置列表
  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/commission-config/levels');
      setConfigs(response.data);
    } catch (error) {
      message.error(error.response?.data?.message || '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载历史记录
  const loadHistory = async (userLevel = null) => {
    setLoading(true);
    try {
      const params = userLevel ? { userLevel } : {};
      const response = await api.get('/admin/commission-config/history', { params });
      setHistory(response.data.records);
    } catch (error) {
      message.error(error.response?.data?.message || '加载历史记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // 打开编辑弹窗
  const handleEdit = (config) => {
    setSelectedConfig(config);
    form.setFieldsValue({
      commissionRate: parseFloat(config.commissionRate),
      description: config.description,
      changeReason: '',
    });
    setEditModalVisible(true);
  };

  // 提交编辑
  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      await api.put(`/admin/commission-config/levels/${selectedConfig.id}`, values);
      
      message.success('配置更新成功');
      setEditModalVisible(false);
      loadConfigs();
    } catch (error) {
      message.error(error.response?.data?.message || '更新失败');
    }
  };

  // 查看历史
  const handleViewHistory = (config) => {
    setSelectedLevel(config.userLevel);
    loadHistory(config.userLevel);
    setHistoryDrawerVisible(true);
  };

  // 查看所有历史
  const handleViewAllHistory = () => {
    setSelectedLevel(null);
    loadHistory();
    setHistoryDrawerVisible(true);
  };

  // 切换状态
  const handleToggleStatus = async (config) => {
    try {
      const newStatus = config.status === 1 ? 0 : 1;
      await api.patch(`/admin/commission-config/levels/${config.id}/status`, {
        status: newStatus,
      });
      message.success(newStatus === 1 ? '配置已启用' : '配置已禁用');
      loadConfigs();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '等级',
      dataIndex: 'userLevel',
      key: 'userLevel',
      width: 80,
      render: (level) => <Tag color="blue">等级{level}</Tag>,
    },
    {
      title: '等级名称',
      dataIndex: 'levelName',
      key: 'levelName',
      width: 120,
    },
    {
      title: '返佣率',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 120,
      render: (rate) => (
        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
          {rate}%
        </span>
      ),
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={() => handleToggleStatus(record)}
        />
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (date) => formatDateTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑配置">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="查看历史">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">分润等级配置</h1>
        <p className="page-description">管理各等级的返佣率配置</p>
      </div>

      <Card>
        <div className="table-toolbar" style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<HistoryOutlined />}
              onClick={handleViewAllHistory}
            >
              查看所有历史
            </Button>
          </Space>
          <div>
            共 {configs.length} 个等级配置
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={configs}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 编辑配置弹窗 */}
      <Modal
        title={`编辑等级配置 - ${selectedConfig?.levelName}`}
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="返佣率（%）"
            name="commissionRate"
            rules={[
              { required: true, message: '请输入返佣率' },
              { type: 'number', min: 0, max: 100, message: '返佣率必须在0-100之间' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              step={0.01}
              precision={2}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            label="说明"
            name="description"
          >
            <Input placeholder="请输入配置说明" />
          </Form.Item>

          <Form.Item
            label="变更原因"
            name="changeReason"
            rules={[{ required: true, message: '请输入变更原因' }]}
          >
            <TextArea
              rows={3}
              placeholder="请输入变更原因，例如：根据市场情况调整返佣率"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 历史记录抽屉 */}
      <Drawer
        title={selectedLevel ? `等级${selectedLevel}配置历史` : '所有配置历史'}
        width={720}
        open={historyDrawerVisible}
        onClose={() => setHistoryDrawerVisible(false)}
      >
        {history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            暂无历史记录
          </div>
        ) : (
          <Timeline>
            {history.map((record) => (
              <Timeline.Item
                key={record.id}
                dot={<CheckCircleOutlined style={{ fontSize: '16px' }} />}
                color="green"
              >
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="等级">
                      <Tag color="blue">等级{record.userLevel}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="返佣率变更">
                      <Space>
                        {record.oldRate && (
                          <>
                            <Tag color="red">{record.oldRate}%</Tag>
                            <span>→</span>
                          </>
                        )}
                        <Tag color="green">{record.newRate}%</Tag>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="变更原因">
                      {record.changeReason || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="操作人">
                      {record.operatorName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="变更时间">
                      {formatDateTime(record.createdAt)}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Drawer>
    </div>
  );
};

export default CommissionLevelConfig;
